"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Loader, Phone, MessageSquare, Mail, MapPin, Clock } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
function JourneyForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-[color:var(--border)] rounded-lg">
                <div className="flex items-center gap-3 mb-4">
                    <span className="inline-block size-1.5 bg-[color:var(--primary)]" />
                    <span className="font-mono text-xs uppercase tracking-[0.22em] text-foreground">Anfrage gesendet</span>
                </div>
                <p className="font-default text-base text-foreground leading-relaxed mb-6">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background font-mono text-xs uppercase tracking-[0.18em] h-auto py-4 px-7 rounded-lg">
                    Weitere Anfrage senden
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
            <FormField control={control} name="full_name" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground/70 mb-2">
                            Name
                        </FormLabel>
                        <FormControl>
                            <Input {...field} placeholder="Max Mustermann" className="border-0 border-b border-foreground/40 bg-transparent px-0 py-3.5 h-auto text-base font-default text-foreground focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30 rounded-lg" />
                        </FormControl>
                        <FormMessage className="font-mono text-xs" />
                    </FormItem>} />

            <FormField control={control} name="email" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground/70 mb-2">
                            E-Mail
                        </FormLabel>
                        <FormControl>
                            <Input {...field} type="email" placeholder="max@beispiel.de" className="border-0 border-b border-foreground/40 bg-transparent px-0 py-3.5 h-auto text-base font-default text-foreground focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30 rounded-lg" />
                        </FormControl>
                        <FormMessage className="font-mono text-xs" />
                    </FormItem>} />

            <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground/70 mb-2">
                            Telefon <span className="normal-case tracking-normal text-foreground/40">— optional</span>
                        </FormLabel>
                        <FormControl>
                            <Input {...field} type="tel" placeholder="+49 123 456789" className="border-0 border-b border-foreground/40 bg-transparent px-0 py-3.5 h-auto text-base font-default text-foreground focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30 rounded-lg" />
                        </FormControl>
                        <FormMessage className="font-mono text-xs" />
                    </FormItem>} />

            <FormField control={control} name="service_type" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground/70 mb-2">
                            Kategorie
                        </FormLabel>
                        <FormControl>
                            <Select onValueChange={field.onChange} value={field.value}>
                                <SelectTrigger className="border-0 border-b border-foreground/40 bg-transparent px-0 py-3.5 h-auto text-base font-default text-foreground focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 data-[placeholder]:text-foreground/30 w-full rounded-lg">
                                    <SelectValue placeholder="Wählen Sie eine Kategorie" />
                                </SelectTrigger>
                                <SelectContent className="border-foreground/40 bg-[color:var(--card)] rounded-lg">
                                    <SelectItem value="Schlüsselservice" className="font-default">Schlüsselservice</SelectItem>
                                    <SelectItem value="Steuergeräte klonen" className="font-default">Steuergeräte klonen</SelectItem>
                                    <SelectItem value="Steuergeräte reparieren" className="font-default">Steuergeräte reparieren</SelectItem>
                                    <SelectItem value="Diagnose" className="font-default">Diagnose</SelectItem>
                                    <SelectItem value="Anlernen & Codieren" className="font-default">Anlernen & Codieren</SelectItem>
                                    <SelectItem value="Servicefunktionen" className="font-default">Servicefunktionen</SelectItem>
                                    <SelectItem value="Reifenservice" className="font-default">Reifenservice</SelectItem>
                                    <SelectItem value="Leistungsoptimierung" className="font-default">Leistungsoptimierung</SelectItem>
                                    <SelectItem value="Softwareanpassung" className="font-default">Softwareanpassung</SelectItem>
                                    <SelectItem value="3D-Scanning & Reverse Engineering" className="font-default">3D-Scanning & Reverse Engineering</SelectItem>
                                    <SelectItem value="CAD-Konstruktion" className="font-default">CAD-Konstruktion</SelectItem>
                                    <SelectItem value="Sonstiges" className="font-default">Sonstiges</SelectItem>
                                </SelectContent>
                            </Select>
                        </FormControl>
                        <FormMessage className="font-mono text-xs" />
                    </FormItem>} />

            <FormField control={control} name="vehicle_brand" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground/70 mb-2">
                            Fahrzeugmarke
                        </FormLabel>
                        <FormControl>
                            <Input {...field} placeholder="z.B. BMW" className="border-0 border-b border-foreground/40 bg-transparent px-0 py-3.5 h-auto text-base font-default text-foreground focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30 rounded-lg" />
                        </FormControl>
                        <FormMessage className="font-mono text-xs" />
                    </FormItem>} />

            <FormField control={control} name="vehicle_model" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground/70 mb-2">
                            Modell
                        </FormLabel>
                        <FormControl>
                            <Input {...field} placeholder="z.B. 3er (F30)" className="border-0 border-b border-foreground/40 bg-transparent px-0 py-3.5 h-auto text-base font-default text-foreground focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30 rounded-lg" />
                        </FormControl>
                        <FormMessage className="font-mono text-xs" />
                    </FormItem>} />

            <FormField control={control} name="vehicle_year" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground/70 mb-2">
                            Baujahr
                        </FormLabel>
                        <FormControl>
                            <Input {...field} type="number" placeholder="z.B. 2015" className="border-0 border-b border-foreground/40 bg-transparent px-0 py-3.5 h-auto text-base font-default text-foreground focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30 rounded-lg" />
                        </FormControl>
                        <FormMessage className="font-mono text-xs" />
                    </FormItem>} />

            <FormField control={control} name="vehicle_engine" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground/70 mb-2">
                            Motorisierung
                        </FormLabel>
                        <FormControl>
                            <Input {...field} placeholder="z.B. 2.0 Diesel" className="border-0 border-b border-foreground/40 bg-transparent px-0 py-3.5 h-auto text-base font-default text-foreground focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30 rounded-lg" />
                        </FormControl>
                        <FormMessage className="font-mono text-xs" />
                    </FormItem>} />

            <FormField control={control} name="vehicle_power" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground/70 mb-2">
                            Leistung (PS/kW)
                        </FormLabel>
                        <FormControl>
                            <Input {...field} placeholder="z.B. 184 PS" className="border-0 border-b border-foreground/40 bg-transparent px-0 py-3.5 h-auto text-base font-default text-foreground focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30 rounded-lg" />
                        </FormControl>
                        <FormMessage className="font-mono text-xs" />
                    </FormItem>} />

            <FormField control={control} name="control_unit_type" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground/70 mb-2">
                            Steuergerät-Typ <span className="normal-case tracking-normal text-foreground/40">— optional</span>
                        </FormLabel>
                        <FormControl>
                            <Input {...field} placeholder="z.B. Bosch EDC17C50" className="border-0 border-b border-foreground/40 bg-transparent px-0 py-3.5 h-auto text-base font-default text-foreground focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30 rounded-lg" />
                        </FormControl>
                        <FormMessage className="font-mono text-xs" />
                    </FormItem>} />

            <FormField control={control} name="key_type" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground/70 mb-2">
                            Schlüssel-Typ <span className="normal-case tracking-normal text-foreground/40">— optional</span>
                        </FormLabel>
                        <FormControl>
                            <Input {...field} placeholder="z.B. Smartkey / Funk" className="border-0 border-b border-foreground/40 bg-transparent px-0 py-3.5 h-auto text-base font-default text-foreground focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30 rounded-lg" />
                        </FormControl>
                        <FormMessage className="font-mono text-xs" />
                    </FormItem>} />

            <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground/70 mb-2">
                            Ihre Nachricht
                        </FormLabel>
                        <FormControl>
                            <Textarea {...field} rows={12} placeholder="Beschreiben Sie Ihr Anliegen..." className="border-0 border-b border-foreground/40 bg-transparent px-0 py-3.5 text-base font-default text-foreground focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0 placeholder:text-foreground/30 resize-none rounded-lg" />
                        </FormControl>
                        <FormMessage className="font-mono text-xs" />
                    </FormItem>} />

            <FormField control={control} name="newsletter" render={({
      field
    }) => <FormItem className="flex flex-row items-start gap-3 space-y-0 pt-2">
                        <FormControl>
                            <Checkbox checked={field.value} onCheckedChange={field.onChange} className="size-4 border border-foreground/60 data-[state=checked]:bg-[color:var(--primary)] data-[state=checked]:border-[color:var(--primary)] data-[state=checked]:text-[color:var(--background)] mt-0.5 rounded-lg" />
                        </FormControl>
                        <FormLabel className="font-default text-sm text-foreground/80 leading-relaxed font-normal cursor-pointer">
                            Ich stimme der Verarbeitung meiner Daten zu.
                        </FormLabel>
                    </FormItem>} />

            {submitError && <div className="p-4 border border-[color:var(--destructive)] rounded-lg">
                    <p className="font-mono text-xs uppercase tracking-[0.18em] text-[color:var(--destructive)]">{submitError}</p>
                </div>}

            <div className="pt-4">
                <Button type="submit" disabled={isSubmitting} className="group relative bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs uppercase tracking-[0.18em] h-auto py-5 px-8 overflow-hidden rounded-lg">
                    {isSubmitting ? <>
                            <Loader className="animate-spin mr-2 size-4" />
                            Wird gesendet...
                        </> : <>
                            <span className="relative z-10 flex items-center gap-2">
                                Anfrage absenden
                                <ArrowRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                            </span>
                            <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-white transition-all duration-300 ease-out group-hover:w-full" />
                        </>}
                </Button>
            </div>
        </Form>;
}
export default function Contact() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const contactMethods = [{
    id: 1,
    label: "Telefon",
    value: "+49 1516 8405221",
    icon: Phone,
    link: "tel:+4915168405221"
  }, {
    id: 2,
    label: "WhatsApp",
    value: "Direktnachricht senden",
    icon: MessageSquare,
    link: "https://wa.me/4915168405221"
  }, {
    id: 3,
    label: "E-Mail",
    value: "carflowde@gmail.com",
    icon: Mail,
    link: "mailto:carflowde@gmail.com"
  }, {
    id: 4,
    label: "Standort",
    value: "CarFlowDE\nRampe 5\n06295 Lutherstadt Eisleben",
    icon: MapPin,
    link: ""
  }, {
    id: 5,
    label: "Öffnungszeiten",
    value: "Flexible Termine nach Absprache",
    icon: Clock,
    link: ""
  }];
  return <section data-section-id="1201" id="express-anfrage" className="bg-background py-32 md:py-44 px-6 md:px-12 lg:px-16">
            <div className="max-w-[1320px] mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div ref={leftRef} className="lg:col-span-5 transition-all duration-700 ease-out" style={{
          opacity: leftVisible ? 1 : 0,
          transform: leftVisible ? "translateY(0)" : "translateY(16px)"
        }}>
                        <div className="flex lg:flex-col flex-row items-start gap-3 mb-10 lg:mb-14">
                            
                            
                        </div>

                        <h2 className="font-default font-extralight text-foreground leading-[0.98] tracking-tight mb-8 !text-7xl" style={{
            fontSize: "clamp(2.75rem, 6.4vw, 6.25rem)"
          }}>Kontakt &amp; Termin</h2>

                        <p className="font-default text-base md:text-[1.0625rem] text-foreground/80 leading-[1.65] max-w-[320px] mb-6">Sie haben eine Anfrage zu unseren KFZ-Services, benötigen Unterstützung bei der Leistungsoptimierung oder Softwareanpassung oder planen ein Projekt im Bereich 3D-Scanning, Reverse Engineering oder CAD-Konstruktion? Auch bei Fragen zu Reifenservice, Steuergeräten oder individuellen Bauteilen helfen wir Ihnen gerne weiter.<div><br /></div><div>Beschreiben Sie Ihr Anliegen kurz über das Kontaktformular oder kontaktieren Sie uns direkt per Telefon oder WhatsApp. Wir prüfen Ihre Anfrage und melden uns schnellstmöglich bei Ihnen zurück.</div></p>

                        

                        <div className="flex items-center gap-6 mb-12 max-w-[420px]">
                            <span className="block h-px bg-[color:var(--secondary)] flex-1" />
                            <span className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground/60 whitespace-nowrap">KONTAKT&nbsp;<br /></span>
                            <span className="size-1.5 bg-[color:var(--primary)] shrink-0" />
                        </div>

                        <div className="space-y-8">
                            {contactMethods.map((method, i) => <div key={method.id} data-index={i} className="flex items-start gap-5 group">
                                    <div className="mt-1 p-2.5 border border-foreground/10 rounded-lg group-hover:border-primary/50 transition-colors duration-300">
                                        <method.icon className="size-5 text-primary" />
                                    </div>
                                    <div>
                                        <div className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground/60 mb-1">
                                            {method.label}
                                        </div>
                                        {method.link ? <Link to={method.link} className="font-default font-light text-lg md:text-xl text-foreground hover:text-primary transition-colors duration-300 block">
                                                {method.value}
                                            </Link> : <div className="font-default font-light text-lg md:text-xl text-foreground">
                                                {method.value}
                                            </div>}
                                    </div>
                                </div>)}
                        </div>
                    </div>

                    <div ref={rightRef} className="lg:col-span-7 transition-all duration-700 ease-out delay-100" style={{
          opacity: rightVisible ? 1 : 0,
          transform: rightVisible ? "translateY(0)" : "translateY(16px)"
        }}>
                        <div className="relative bg-[color:var(--card)] p-8 md:p-12 rounded-lg">
                            <span className="absolute top-0 left-0 w-3 h-3 border-t border-l border-foreground/60" aria-hidden="true" />
                            <span className="absolute top-0 right-0 w-3 h-3 border-t border-r border-foreground/60" aria-hidden="true" />
                            <span className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-foreground/60" aria-hidden="true" />
                            <span className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-foreground/60" aria-hidden="true" />

                            <div className="flex items-center justify-between mb-10 pb-6 border-b border-foreground/15">
                                <span className="font-mono text-[0.6875rem] uppercase tracking-[0.22em] text-foreground/60">Anfrage senden</span>
                                <span className="font-mono text-[0.6875rem] tracking-[0.16em] text-foreground/50">
                                    CARFLOW_DE
                                </span>
                            </div>

                            <FormProvider formKey="express_inquiry_contact" formId="express_inquiry_contact" sectionName="express-anfrage" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              service_type: "",
              vehicle_brand: "",
              vehicle_model: "",
              vehicle_year: "",
              vehicle_engine: "",
              vehicle_power: "",
              control_unit_type: "",
              key_type: "",
              message: "",
              newsletter: false
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              service_type: {
                required: true
              },
              vehicle_brand: {
                required: true
              },
              vehicle_model: {
                required: true
              },
              vehicle_year: {
                required: true
              },
              vehicle_engine: {
                required: true
              },
              vehicle_power: {
                required: true
              },
              message: {
                maxLength: 500
              }
            }} submitText="Anfrage absenden" successMessage="Vielen Dank für Ihre Anfrage. Wir melden uns zeitnah bei Ihnen zurück." errorMessage="Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut." className="rounded-lg">
                                <JourneyForm />
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </div>
        </section>;
}